import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Check, Copy, X } from "lucide-react";

/* Small primitive layer for Heartline Ops. Everything visual is Tailwind
 * utilities on the tokens in styles.css — these components only add the
 * behavior (toasts, modal scaffold, tabs, copy) so pages stay declarative. */

// ── Toasts ────────────────────────────────────────────────────────────

type ToastKind = "ok" | "err";
type ToastFn = (kind: ToastKind, msg: string) => void;

const ToastCtx = createContext<ToastFn>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<{ id: number; kind: ToastKind; msg: string }[]>([]);
  const push = useCallback<ToastFn>((kind, msg) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="rise-in pointer-events-auto flex items-center gap-2 rounded-(--radius-field) bg-ink-800 px-3.5 py-2.5 text-sm shadow-(--shadow-overlay)"
          >
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.kind === "ok" ? "bg-ok" : "bg-err"}`}
            />
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-950/70 p-4 pt-[8vh] backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`overlay-in card w-full bg-ink-800 shadow-(--shadow-overlay) ${wide ? "max-w-2xl" : "max-w-lg"}`}
        role="dialog"
        aria-modal
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-1">
          <h2 className="text-base font-semibold">{title}</h2>
          <button className="btn-ghost -mr-2 !p-1.5" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 pb-5">{children}</div>
      </div>
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: ReactNode }[];
  value: T;
  onChange: (t: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-(--radius-field) bg-ink-900 p-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`flex-1 rounded-[7px] px-3 py-1.5 text-[13px] font-medium transition ${
            value === t.id ? "bg-ink-700 text-fg" : "text-fg-mute hover:text-fg"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, string> = {
  open: "bg-warn/15 text-warn",
  ack: "bg-info/15 text-info",
  fixed: "bg-ok/15 text-ok",
  wontfix: "bg-ink-700 text-fg-mute",
};
export const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  ack: "Acknowledged",
  fixed: "Fixed",
  wontfix: "Won't fix",
};

export function StatusPill({ status }: { status: string }) {
  return (
    <span className={`pill ${STATUS_STYLE[status] ?? "bg-ink-700 text-fg-mute"}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

// ── Copy button ───────────────────────────────────────────────────────

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn-quiet !px-2.5 !py-1.5 text-xs"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <Check className="h-3.5 w-3.5 text-ok" /> : <Copy className="h-3.5 w-3.5" />}
      {label ?? (done ? "Copied" : "Copy")}
    </button>
  );
}

// ── Empty / loading states ────────────────────────────────────────────

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="py-16 text-center">
      <div className="text-sm font-medium text-fg-mute">{title}</div>
      {hint && <div className="mt-1 text-[13px] text-fg-faint">{hint}</div>}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="py-16 text-center text-sm text-fg-faint">{label}</div>;
}

// ── Formatters ────────────────────────────────────────────────────────

export function timeAgo(ms?: number | null): string {
  if (!ms) return "—";
  const d = Date.now() - ms;
  if (d < 60_000) return "just now";
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  if (d < 604_800_000) return `${Math.floor(d / 86_400_000)}d ago`;
  return new Date(ms).toLocaleDateString();
}

export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
